import { Book, Annotation } from '../types';
import { dbManager } from './db_manager';

const BOOKS_KEY = 'libritui_books';
const ANNOTATIONS_KEY = 'libritui_annotations';
const PENDING_PROGRESS_KEY = 'libritui_pending_progress';

let progressDebounceTimer: any = null;

export const storageService = {
  getBooks: (): Book[] => {
    const data = localStorage.getItem(BOOKS_KEY);
    const books: Book[] = data ? (JSON.parse(data) as Book[]) : [];
    return books.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  },

  saveBook: (book: Book) => {
    const books = storageService.getBooks();
    const index = books.findIndex(b => b.id === book.id);
    if (index >= 0) {
      books[index] = { ...books[index], ...book };
    } else {
      const maxOrder = books.reduce((max, b) => Math.max(max, b.order ?? 0), 0);
      book.order = maxOrder + 1;
      books.push(book);
    }
    try {
      localStorage.setItem(BOOKS_KEY, JSON.stringify(books));
    } catch (e) {
      console.error("[StorageService] Error saving book to localStorage:", e);
    }
  },

  updateBooksBatch: (books: Book[]) => {
    try {
      localStorage.setItem(BOOKS_KEY, JSON.stringify(books));
    } catch (e) {
      console.error("[StorageService] Error batch updating books in localStorage:", e);
    }
  },

  deleteBook: (id: string) => {
    const books = storageService.getBooks().filter(b => b.id !== id);
    try {
      localStorage.setItem(BOOKS_KEY, JSON.stringify(books));
    } catch (e) {
      console.error("[StorageService] Error deleting book from localStorage:", e);
    }
  },

  getAnnotations: (bookId: string): Annotation[] => {
    const data = localStorage.getItem(ANNOTATIONS_KEY);
    const all: Annotation[] = data ? (JSON.parse(data) as Annotation[]) : [];
    return all.filter(a => a.bookId === bookId).sort((a, b) => b.createdAt - a.createdAt);
  },

  saveAnnotation: (annotation: Annotation) => {
    const data = localStorage.getItem(ANNOTATIONS_KEY);
    const all: Annotation[] = data ? (JSON.parse(data) as Annotation[]) : [];
    all.push(annotation);
    try {
      localStorage.setItem(ANNOTATIONS_KEY, JSON.stringify(all));
    } catch (e) {
      console.error("[StorageService] Error saving annotation to localStorage:", e);
    }
  },

  updateAnnotation: (id: string, updates: Partial<Annotation>) => {
    const data = localStorage.getItem(ANNOTATIONS_KEY);
    let all: Annotation[] = data ? (JSON.parse(data) as Annotation[]) : [];
    all = all.map(a => a.id === id ? { ...a, ...updates } : a);
    try {
      localStorage.setItem(ANNOTATIONS_KEY, JSON.stringify(all));
    } catch (e) {
      console.error("[StorageService] Error updating annotation in localStorage:", e);
    }
  },

  deleteAnnotation: (id: string) => {
    const data = localStorage.getItem(ANNOTATIONS_KEY);
    let all: Annotation[] = data ? (JSON.parse(data) as Annotation[]) : [];
    all = all.filter(a => a.id !== id);
    try {
      localStorage.setItem(ANNOTATIONS_KEY, JSON.stringify(all));
    } catch (e) {
      console.error("[StorageService] Error deleting annotation from localStorage:", e);
    }
  },

  // --- New Global Annotations Logic (Safe Haven) ---

  saveAnnotationGlobal: async (userId: string | undefined, annotation: Annotation) => {
    // 1. Save locally as before
    storageService.saveAnnotation(annotation);

    // 2. Sync to Cloudflare D1 if logged in
    if (userId) {
      try {
        const { translationService } = await import('./translationService');
        // Parse content to extract original and translation
        const parts = annotation.content.split(' -> ');
        const original = parts[0] || '';
        const meta = parts[1] || '';
        const translationValue = meta.split('|~|')[0]?.trim() || '';

        await translationService.saveUserNote(userId, {
          id: annotation.id,
          book_id: annotation.bookId,
          book_title: 'Livro', // Fallback as we don't have it here easily
          original: original,
          translation: translationValue,
          page_number: annotation.pageNumber,
          favorited: annotation.favorited || 'no'
        });
        console.log("[D1] Annotation synced successfully to user_notes.");
      } catch (error) {
        console.error("[D1] Error saving global annotation:", error);
      }
    }
  },

  syncAnnotations: async (userId: string, bookId: string): Promise<Annotation[]> => {
    if (!navigator.onLine) return [];

    try {
      const { translationService } = await import('./translationService');
      const results = await translationService.getUserNotes(userId, bookId);

      // results is a direct array from Worker
      if (!Array.isArray(results)) return [];

      const mapped: Annotation[] = results.map((a: any) => ({
        id: String(a.id),
        bookId: String(a.book_id),
        pageNumber: a.page_number || 1,
        content: `${a.text || a.original_text || ''} -> ${a.translation || a.translated_text || ''}`,
        favorited: a.favorited || 'no',
        type: 'note',
        color: 'primary',
        createdAt: a.created_at ? new Date(a.created_at).getTime() : Date.now()
      }));
      return mapped;
    } catch (error) {
      console.error("[D1] Error syncing annotations:", error);
      return [];
    }
  },

  // --- New Scalable Progress Logic ---

  saveBookProgress: async (userId: string | undefined, bookId: string, page: number, totalPages: number, source: string = 'library') => {
    const timestamp = Date.now();
    const progress = totalPages > 0 ? Math.round((page / totalPages) * 100) : 0;

    // Preserve existing source if already stored
    let bookSource = source;
    try {
      const existingRaw = localStorage.getItem(`progresso_${bookId}`);
      if (existingRaw) {
        const parsed = JSON.parse(existingRaw);
        if (parsed?.source) bookSource = parsed.source;
      }
    } catch {}

    // 1. Immediate Local Save (Updates 'books' array for legacy compatibility and fast UI)
    try {
      const books = storageService.getBooks();
      const index = books.findIndex(b => b.id === bookId);
      if (index >= 0) {
        books[index].lastPage = page;
        books[index].progress = progress;
        localStorage.setItem(BOOKS_KEY, JSON.stringify(books));
      }
      // Also save to a dedicated fast-access key for this book's progress
      localStorage.setItem(`progresso_${bookId}`, JSON.stringify({ page, percentage: progress, timestamp, active: true, source: bookSource }));
    } catch (e) {
      console.error("Local save error:", e);
    }

    // 2. Queue for Sync (Offline-First)
    if (userId && userId !== 'guest') {
      const pendingMap = JSON.parse(localStorage.getItem(PENDING_PROGRESS_KEY) || '{}');
      pendingMap[bookId] = {
        userId,
        bookId,
        page,
        totalPages,
        percentage: progress,
        timestamp,
        source: bookSource
      };
      try {
        localStorage.setItem(PENDING_PROGRESS_KEY, JSON.stringify(pendingMap));
      } catch (e) {
        console.error("[StorageService] Error saving pending progress:", e);
      }

      // 3. Debounce Sync (5 seconds)
      if (progressDebounceTimer) clearTimeout(progressDebounceTimer);
      progressDebounceTimer = setTimeout(() => {
        storageService.syncPendingProgress();
      }, 5000);
    }
  },

  syncPendingProgress: async () => {
    if (!navigator.onLine) return;

    try {
      const pendingMap = JSON.parse(localStorage.getItem(PENDING_PROGRESS_KEY) || '{}');
      const keys = Object.keys(pendingMap);
      if (keys.length === 0) return;

      console.log(`[Sync] Syncing ${keys.length} pending progress items...`);

      // Process each pending item
      for (const key of keys) {
        const item = pendingMap[key];
        const { userId, bookId, page, totalPages, percentage, timestamp, source } = item;

        if (!userId || userId === 'undefined') {
          console.warn("[Sync] User ID inválido, ignorando item:", item);
          continue;
        }

        const payload = {
          user_id: userId,
          book_id: bookId,
          current_page: page,
          percentage: percentage,
          last_read: new Date(timestamp).toISOString(),
          updated_at: new Date().toISOString(),
          source: source || 'library'
        };

        try {
          await dbManager.from('user_progress', userId).upsert(payload);
          delete pendingMap[key];
        } catch (error: any) {
          console.error(`[Sync] Failed to sync ${bookId} to D1:`, error.message);
        }
      }

      try {
        localStorage.setItem(PENDING_PROGRESS_KEY, JSON.stringify(pendingMap));
      } catch (e) {
        console.error("[StorageService] Error updating pending progress in localStorage:", e);
      }
    } catch (err) {
      console.error("[Sync] Validating sync error:", err);
    }
  },

  // "Winner" Logic Helper
  getBestProgress: async (userId: string | undefined, bookId: string): Promise<{ page: number, timestamp: number } | null> => {
    // 1. Get Local
    let localData: any = null;
    try {
      const raw = localStorage.getItem(`progresso_${bookId}`);
      if (raw) localData = JSON.parse(raw);
    } catch { }

    // 2. Get Remote (if user)
    let remoteData: any = null;
    if (userId && userId !== 'guest') {
      try {
        const results = await dbManager.from('user_progress', userId).select({ book_id: bookId });
        const data = results && results.length > 0 ? results[0] : null;

        if (data) {
          remoteData = {
            page: data.current_page,
            timestamp: new Date(data.last_read).getTime()
          };
        }
      } catch (err) {
        console.error("[D1] Error fetching best progress:", err);
      }
    }

    // 3. Compare ("Winner")
    if (localData && remoteData) {
      if (remoteData.timestamp > localData.timestamp) {
        // Remote wins - Update local to match
        return { page: remoteData.page, timestamp: remoteData.timestamp };
      } else {
        // Local wins (or equal) - Ensure remote is eventually updated (already handled by queue, but good to know)
        return { page: localData.page || localData.pageNumber || 1, timestamp: localData.timestamp || 0 };
      }
    } else if (remoteData) {
      return { page: remoteData.page, timestamp: remoteData.timestamp };
    } else if (localData) {
      return { page: localData.page || localData.pageNumber || 1, timestamp: localData.timestamp || 0 };
    }

    return null;
  },

  // --- Creative Mode (Handwriting) ---

  getUserAnnotations: async (userId: string | undefined, bookId: string): Promise<any[]> => {
    if (!userId || userId === 'guest' || userId === 'undefined' || userId === 'null') return [];
    if (!bookId || bookId === 'undefined' || bookId === 'null') {
        console.warn("[StorageService] getUserAnnotations: Invalid bookId", bookId);
        return [];
    }

    try {
      const result = await dbManager.userAnnotations.get(userId, bookId);

      if (result && result.content) {
        return JSON.parse(result.content);
      }
      return [];
    } catch (err) {
      console.error("[D1] Error fetching drawing annotations:", err);
      return [];
    }
  },

  saveUserAnnotations: async (userId: string | undefined, bookId: string, drawings: any[]) => {
    if (!userId || userId === 'guest') return;

    try {
      await dbManager.userAnnotations.upsert(userId, bookId, JSON.stringify(drawings));
      console.log("[D1] Drawings synced to cloud.");
    } catch (err) {
      console.error("[D1] Error saving drawing annotations:", err);
    }
  }
};

// Auto-retry sync when online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log("[Sync] Online detected. Retrying pending progress...");
    storageService.syncPendingProgress();
  });
}
